import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Plus, X, Edit3, Save, User, Phone, Mail, Linkedin, FileText, Upload, File, Trash2, AlertCircle, Building2 } from 'lucide-react';
import { SearchableSelect, SelectOption } from './SearchableSelect';
import cloudinaryService from '../../services/cloudinaryService';
import {
  getFunctionsForPersonType,
  CompanyTypeForFunctions,
  PersonTypeForFunctions
} from '../../constants/personnelFunctions';
import type { HolderType, PersonData } from '../../types/company';

// Types de participation selon la forme juridique OHADA
export type ShareType = 'actions' | 'parts_sociales' | 'proprietaire_unique' | 'parts_gie' | 'none';

// Mapping des formes juridiques OHADA vers le type de participation
export const getShareTypeFromLegalForm = (legalForm?: string): ShareType => {
  if (!legalForm) return 'none';
  
  switch (legalForm) {
    // Sociétés par actions
    case 'SAAG': // SA avec Administrateur Général
    case 'SACA': // SA avec Conseil d'Administration
    case 'SAS':  // Société par Actions Simplifiée
    case 'SAU':  // Société Anonyme Unipersonnelle
      return 'actions';
    
    // Sociétés à parts sociales
    case 'SARL': // Société À Responsabilité Limitée
    case 'SARLU': // SARL Unipersonnelle
    case 'SNC':  // Société en Nom Collectif
    case 'SCS':  // Société en Commandite Simple
    case 'SP':   // Société en Participation
      return 'parts_sociales';
    
    // Entreprise individuelle
    case 'EI':
      return 'proprietaire_unique';
    
    // GIE
    case 'GIE':
      return 'parts_gie';
    
    default:
      return 'none';
  }
};

// Labels selon le type de participation
export const getShareLabels = (shareType: ShareType) => {
  switch (shareType) {
    case 'actions':
      return {
        title: 'Actionnaires',
        personLabel: 'Actionnaire',
        shareLabel: 'Nombre d\'actions',
        percentLabel: '% du capital',
        fonctions: ['Actionnaire majoritaire', 'Actionnaire minoritaire', 'Fondateur', 'Investisseur', 'Administrateur'],
        placeholder: 'Aucun actionnaire enregistré'
      };
    case 'parts_sociales':
      return {
        title: 'Associés',
        personLabel: 'Associé',
        shareLabel: 'Nombre de parts sociales',
        percentLabel: '% des parts',
        fonctions: ['Associé gérant', 'Associé majoritaire', 'Associé minoritaire', 'Fondateur', 'Commandité', 'Commanditaire'],
        placeholder: 'Aucun associé enregistré'
      };
    case 'proprietaire_unique':
      return {
        title: 'Propriétaire',
        personLabel: 'Propriétaire',
        shareLabel: '',
        percentLabel: '',
        fonctions: ['Propriétaire exploitant'],
        placeholder: 'Propriétaire non renseigné'
      };
    case 'parts_gie':
      return {
        title: 'Membres du GIE',
        personLabel: 'Membre',
        shareLabel: 'Quote-part',
        percentLabel: '% de participation',
        fonctions: ['Membre fondateur', 'Membre adhérent', 'Administrateur'],
        placeholder: 'Aucun membre enregistré'
      };
    default:
      return {
        title: 'Actionnaires/Associés',
        personLabel: 'Personne',
        shareLabel: 'Parts/Actions',
        percentLabel: '% du capital',
        fonctions: [],
        placeholder: 'Veuillez d\'abord sélectionner la forme juridique'
      };
  }
};

// Type de détenteur (personne physique ou morale) et personne :
// source unique dans types/company.
export type { HolderType };
type Person = PersonData;

interface PersonInputProps {
  label: string;
  value: Person[];
  onSave: (persons: Person[]) => void;
  disabled?: boolean;
  placeholder?: string;
  personType?: 'dirigeant' | 'employe' | 'actionnaire';
  showEquity?: boolean;
  showContract?: boolean;
  showSalary?: boolean;
  formeJuridique?: string; // Forme juridique OHADA pour adapter l'affichage
  companyType?: 'traditional' | 'startup'; // Type d'entreprise pour adapter les fonctions
}

// Composant interne pour le drag & drop de CV
const CVDropZone: React.FC<{
  cv?: string;
  cvFileName?: string;
  onFileChange: (url: string, fileName: string) => void;
  onRemove: () => void;
  disabled?: boolean;
}> = ({ cv, cvFileName, onFileChange, onRemove, disabled }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFile = async (file: File) => {
    // Valider le type de fichier
    const validTypes = ['.pdf', '.doc', '.docx'];
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (!validTypes.includes(extension)) {
      alert('Format non accepté. Utilisez PDF, DOC ou DOCX.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Le fichier ne doit pas dépasser 5MB.');
      return;
    }

    setIsUploading(true);
    
    try {
      // Upload vers Cloudinary
      const result = await cloudinaryService.uploadCV(file);
      
      console.log('✅ CV uploadé sur Cloudinary:', result.url);
      onFileChange(result.url, file.name);
    } catch (error) {
      console.error('❌ Erreur upload CV:', error);
      alert(error instanceof Error ? error.message : 'Erreur lors de l\'upload du CV');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const handleBrowseClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFile(e.target.files[0]);
    }
  };

  // Si un CV est déjà uploadé
  if (cv) {
    return (
      <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex items-center space-x-3">
          <File className="w-5 h-5 text-red-500" />
          <div>
            <a 
              href={cv}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-[#197ca8] hover:underline"
            >
              {cvFileName || 'CV.pdf'}
            </a>
          </div>
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={onRemove}
            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={handleBrowseClick}
      className={`
        relative border-2 border-dashed rounded-lg p-4 text-center transition-all duration-200 cursor-pointer
        ${isDragging 
          ? 'border-[#197ca8] bg-blue-50' 
          : 'border-gray-300 hover:border-gray-400'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx"
        onChange={handleFileInput}
        className="hidden"
        disabled={disabled}
      />

      {isUploading ? (
        <div className="flex items-center justify-center space-x-2 py-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#197ca8]"></div>
          <span className="text-sm text-gray-600">Upload en cours...</span>
        </div>
      ) : (
        <div className="flex flex-col items-center space-y-1">
          <Upload className={`w-6 h-6 ${isDragging ? 'text-[#197ca8]' : 'text-gray-400'}`} />
          <div className="text-xs">
            <span className="font-medium text-[#197ca8]">Cliquez</span>
            <span className="text-gray-500"> ou glissez le CV ici</span>
          </div>
          <div className="text-xs text-gray-400">
            PDF, DOC, DOCX • Max 5MB
          </div>
        </div>
      )}
    </div>
  );
};

export const PersonInput: React.FC<PersonInputProps> = ({
  label,
  value = [],
  onSave,
  disabled = false,
  placeholder = "Ajouter une personne",
  personType = 'dirigeant',
  showEquity = false,
  showContract = false,
  formeJuridique,
  companyType = 'traditional'
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [persons, setPersons] = useState<Person[]>(value);
  const [newPerson, setNewPerson] = useState<Partial<Person>>({});
  
  // ✨ SYNC: Synchroniser persons quand value change depuis le parent
  // IMPORTANT: Faire une copie profonde pour éviter les mutations croisées entre composants
  useEffect(() => {
    if (!isEditing) {
      // Copie profonde pour isoler chaque instance du composant
      const copiedPersons = (value || []).map(p => ({ ...p }));
      setPersons(copiedPersons);
    }
  }, [value, isEditing, personType]);
  
  // Déterminer le type de participation selon la forme juridique
  const shareType = personType === 'actionnaire' ? getShareTypeFromLegalForm(formeJuridique) : 'none';
  const shareLabels = getShareLabels(shareType);

  // Obtenir les options de fonctions selon le type d'entreprise et de personne
  const fonctionOptions = useMemo((): SelectOption[] => {
    const functions = getFunctionsForPersonType(
      companyType as CompanyTypeForFunctions,
      personType as PersonTypeForFunctions,
      shareType
    );
    return functions.map(f => ({
      value: f.value,
      label: f.label,
      description: f.description,
      category: f.category
    }));
  }, [companyType, personType, shareType]);

  const handleStartEditing = () => {
    setIsEditing(true);
    // IMPORTANT: Copie profonde pour éviter les mutations croisées
    const copiedPersons = (value || []).map(p => ({ ...p }));
    setPersons(copiedPersons);
    setNewPerson({});
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    // IMPORTANT: Copie profonde pour restaurer correctement
    const copiedPersons = (value || []).map(p => ({ ...p }));
    setPersons(copiedPersons);
    setNewPerson({});
  };

  const handleSaveEditing = () => {
    // IMPORTANT: Copie profonde pour s'assurer que les données sont bien isolées
    const personsToSave = persons.map(p => ({ ...p }));
    onSave(personsToSave);
    setIsEditing(false);
    setNewPerson({});
  };

  const handleAddPerson = () => {
    const isPersonneMorale = newPerson.typeDetenteur === 'morale';
    
    // Validation selon le type
    const isValid = isPersonneMorale 
      ? newPerson.raisonSociale && newPerson.fonction
      : newPerson.nom && newPerson.prenoms && newPerson.fonction;
    
    if (isValid) {
      const person: Person = {
        id: Date.now().toString(),
        typeDetenteur: newPerson.typeDetenteur || 'physique',
        // Personne physique
        nom: newPerson.nom || '',
        prenoms: newPerson.prenoms || '',
        fonction: newPerson.fonction || '',
        nationalite: newPerson.nationalite || '',
        telephone: newPerson.telephone || '',
        email: newPerson.email || '',
        adresse: newPerson.adresse || '',
        linkedin: newPerson.linkedin || '',
        cv: newPerson.cv || '',
        cvFileName: newPerson.cvFileName || '',
        // Personne morale
        raisonSociale: newPerson.raisonSociale || '',
        formeJuridique: newPerson.formeJuridique || '',
        rccm: newPerson.rccm || '',
        idnat: newPerson.idnat || '',
        siegeSocial: newPerson.siegeSocial || '',
        representantLegal: newPerson.representantLegal || '',
        representantFonction: newPerson.representantFonction || '',
        // Commun
        nombreActions: newPerson.nombreActions || 0,
        pourcentageActions: newPerson.pourcentageActions || 0,
        dateNomination: newPerson.dateNomination || '',
        typeContrat: newPerson.typeContrat || '',
        salaire: newPerson.salaire || 0,
        diplomes: newPerson.diplomes || []
      };
      setPersons([...persons, person]);
      setNewPerson({});
    }
  };

  const handleRemovePerson = (index: number) => {
    const updatedPersons = persons.filter((_, i) => i !== index);
    setPersons(updatedPersons);
  };

  const handleEditPerson = <K extends keyof Person>(index: number, field: K, value: Person[K]) => {
    // Mise à jour fonctionnelle : deux appels successifs (cv puis cvFileName)
    // partaient de la même copie périmée, le second effaçait le premier.
    setPersons(prev => {
      const updatedPersons = [...prev];
      updatedPersons[index] = { ...updatedPersons[index], [field]: value };
      return updatedPersons;
    });
  };

  const getPersonTypeLabel = () => {
    if (personType === 'actionnaire' && shareType !== 'none') {
      return shareLabels.personLabel;
    }
    switch (personType) {
      case 'dirigeant': return 'Dirigeant';
      case 'employe': return 'Employé';
      case 'actionnaire': return 'Actionnaire';
      default: return 'Personne';
    }
  };

  // Obtenir le label affiché d'une fonction (valeur ou label)
  const getFunctionDisplayLabel = (value: string): string => {
    const found = fonctionOptions.find(opt => opt.value === value || opt.label === value);
    return found?.label || value;
  };

  // Si c'est un actionnaire et que la forme juridique n'est pas définie
  if (personType === 'actionnaire' && shareType === 'none' && !disabled) {
    return (
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                Forme juridique requise
              </p>
              <p className="text-sm text-amber-700 mt-1">
                Veuillez d'abord sélectionner la <strong>forme juridique OHADA</strong> de l'entreprise 
                dans la section "Identité de l'entreprise" pour pouvoir renseigner la structure actionnariale.
              </p>
              <p className="text-xs text-amber-600 mt-2">
                Le type de participation (actions, parts sociales, etc.) dépend de la forme juridique choisie.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Si c'est une entreprise individuelle, afficher un message spécifique
  if (personType === 'actionnaire' && shareType === 'proprietaire_unique') {
    return (
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          {shareLabels.title}
        </label>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <User className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-800">
                Entreprise Individuelle
              </p>
              <p className="text-sm text-blue-700 mt-1">
                En tant qu'entreprise individuelle, vous êtes le <strong>propriétaire unique</strong> de l'établissement.
                Il n'y a pas de capital social divisé en parts ou actions.
              </p>
              <p className="text-xs text-blue-600 mt-2">
                Les informations du propriétaire sont celles de l'utilisateur principal du compte.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isEditing) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">
            {personType === 'actionnaire' ? shareLabels.title : label}
          </label>
          {!disabled && (
            <button
              onClick={handleStartEditing}
              className="text-[#197ca8] hover:text-[#156a92] text-sm flex items-center"
            >
              <Edit3 className="w-4 h-4 mr-1" />
              Modifier
            </button>
          )}
        </div>
        
        {value.length === 0 ? (
          <div className="text-gray-500 text-sm italic p-4 border-2 border-dashed border-gray-200 rounded-lg text-center">
            {placeholder}
          </div>
        ) : (
          <div className="space-y-3">
            {value.map((person) => (
              <div key={person.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      {person.typeDetenteur === 'morale' ? (
                        <>
                          <Building2 className="w-4 h-4 text-[#197ca8] mr-2" />
                          <span className="font-medium text-gray-900">
                            {person.raisonSociale}
                          </span>
                          {person.formeJuridique && (
                            <span className="ml-1 text-xs text-gray-500">({person.formeJuridique})</span>
                          )}
                        </>
                      ) : (
                        <>
                          <User className="w-4 h-4 text-[#197ca8] mr-2" />
                          <span className="font-medium text-gray-900">
                            {person.prenoms} {person.nom}
                          </span>
                        </>
                      )}
                      <span className="ml-2 px-2 py-1 bg-[#197ca8] text-white text-xs rounded">
                        {getFunctionDisplayLabel(person.fonction)}
                      </span>
                      {person.typeDetenteur === 'morale' && (
                        <span className="ml-1 px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">
                          Personne morale
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                      {/* Infos personne morale */}
                      {person.typeDetenteur === 'morale' && (
                        <>
                          {person.rccm && (
                            <div>RCCM: {person.rccm}</div>
                          )}
                          {person.idnat && (
                            <div>ID Nat: {person.idnat}</div>
                          )}
                          {person.siegeSocial && (
                            <div className="md:col-span-2">Siège: {person.siegeSocial}</div>
                          )}
                          {person.representantLegal && (
                            <div className="md:col-span-2 flex items-center">
                              <User className="w-3 h-3 mr-1" />
                              Représentant: {person.representantLegal}
                              {person.representantFonction && ` (${person.representantFonction})`}
                            </div>
                          )}
                        </>
                      )}
                      
                      {/* Infos personne physique */}
                      {person.telephone && (
                        <div className="flex items-center">
                          <Phone className="w-3 h-3 mr-1" />
                          {person.telephone}
                        </div>
                      )}
                      {person.email && (
                        <div className="flex items-center">
                          <Mail className="w-3 h-3 mr-1" />
                          {person.email}
                        </div>
                      )}
                      {person.linkedin && person.typeDetenteur !== 'morale' && (
                        <a 
                          href={person.linkedin} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center text-[#0077B5] hover:underline"
                        >
                          <Linkedin className="w-3 h-3 mr-1" />
                          LinkedIn
                        </a>
                      )}
                      {person.cv && person.typeDetenteur !== 'morale' && (
                        <a 
                          href={person.cv} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center text-[#197ca8] hover:underline"
                        >
                          <File className="w-3 h-3 mr-1 text-red-500" />
                          {person.cvFileName || 'Voir CV'}
                        </a>
                      )}
                      {person.nationalite && (
                        <div>Nationalité: {person.nationalite}</div>
                      )}
                      {showEquity && (person.nombreActions || person.pourcentageActions) && (
                        <div className="font-medium text-[#197ca8] md:col-span-2 bg-blue-50 px-2 py-1 rounded">
                          {person.nombreActions && (
                            <span>{shareLabels.shareLabel}: {person.nombreActions.toLocaleString()}</span>
                          )}
                          {person.nombreActions && person.pourcentageActions && <span className="mx-2">•</span>}
                          {person.pourcentageActions && (
                            <span>{shareLabels.percentLabel}: {person.pourcentageActions}%</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          {personType === 'actionnaire' ? shareLabels.title : label}
        </label>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleCancelEditing}
            className="text-gray-600 hover:text-gray-800 text-sm flex items-center"
          >
            <X className="w-4 h-4 mr-1" />
            Annuler
          </button>
          <button
            onClick={handleSaveEditing}
            className="text-green-600 hover:text-green-800 text-sm flex items-center"
          >
            <Save className="w-4 h-4 mr-1" />
            Sauvegarder
          </button>
        </div>
      </div>

      {/* Liste des personnes existantes */}
      <div className="space-y-3">
        {persons.map((person, index) => (
          <div key={person.id} className="bg-white border-2 border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                {person.typeDetenteur === 'morale' ? (
                  <Building2 className="w-4 h-4 text-purple-600" />
                ) : (
                  <User className="w-4 h-4 text-[#197ca8]" />
                )}
                <span className="font-medium text-gray-900">
                  {getPersonTypeLabel()} #{index + 1}
                </span>
                {person.typeDetenteur === 'morale' && (
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                    Personne morale
                  </span>
                )}
              </div>
              <button
                onClick={() => handleRemovePerson(index)}
                className="text-red-600 hover:text-red-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Sélecteur type de détenteur pour les actionnaires */}
            {personType === 'actionnaire' && (
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-700 mb-2">Type de détenteur</label>
                <div className="flex space-x-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name={`typeDetenteur-${person.id}`}
                      checked={person.typeDetenteur !== 'morale'}
                      onChange={() => handleEditPerson(index, 'typeDetenteur', 'physique')}
                      className="mr-2 text-[#197ca8]"
                    />
                    <User className="w-4 h-4 mr-1 text-gray-600" />
                    <span className="text-sm">Personne physique</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name={`typeDetenteur-${person.id}`}
                      checked={person.typeDetenteur === 'morale'}
                      onChange={() => handleEditPerson(index, 'typeDetenteur', 'morale')}
                      className="mr-2 text-purple-600"
                    />
                    <Building2 className="w-4 h-4 mr-1 text-gray-600" />
                    <span className="text-sm">Personne morale</span>
                  </label>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Champs pour personne morale */}
              {person.typeDetenteur === 'morale' ? (
                <>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Raison sociale *</label>
                    <input
                      type="text"
                      value={person.raisonSociale || ''}
                      onChange={(e) => handleEditPerson(index, 'raisonSociale', e.target.value)}
                      placeholder="Nom de l'entreprise"
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Forme juridique</label>
                    <select
                      value={person.formeJuridique || ''}
                      onChange={(e) => handleEditPerson(index, 'formeJuridique', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    >
                      <option value="">Sélectionner...</option>
                      <option value="SARL">SARL</option>
                      <option value="SA">SA</option>
                      <option value="SAS">SAS</option>
                      <option value="SNC">SNC</option>
                      <option value="GIE">GIE</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Fonction/Qualité *</label>
                    <SearchableSelect
                      value={person.fonction}
                      onChange={(val) => handleEditPerson(index, 'fonction', val)}
                      options={fonctionOptions}
                      placeholder="Rechercher une fonction..."
                      allowCustom={true}
                      groupByCategory={false}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">N° RCCM</label>
                    <input
                      type="text"
                      value={person.rccm || ''}
                      onChange={(e) => handleEditPerson(index, 'rccm', e.target.value)}
                      placeholder="CD/KIN/RCCM/..."
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">ID National</label>
                    <input
                      type="text"
                      value={person.idnat || ''}
                      onChange={(e) => handleEditPerson(index, 'idnat', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Siège social</label>
                    <input
                      type="text"
                      value={person.siegeSocial || ''}
                      onChange={(e) => handleEditPerson(index, 'siegeSocial', e.target.value)}
                      placeholder="Adresse du siège social"
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Représentant légal</label>
                    <input
                      type="text"
                      value={person.representantLegal || ''}
                      onChange={(e) => handleEditPerson(index, 'representantLegal', e.target.value)}
                      placeholder="Nom complet"
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Fonction du représentant</label>
                    <input
                      type="text"
                      value={person.representantFonction || ''}
                      onChange={(e) => handleEditPerson(index, 'representantFonction', e.target.value)}
                      placeholder="Ex: Directeur Général"
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Téléphone</label>
                    <input
                      type="tel"
                      value={person.telephone || ''}
                      onChange={(e) => handleEditPerson(index, 'telephone', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={person.email || ''}
                      onChange={(e) => handleEditPerson(index, 'email', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* Champs pour personne physique */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Nom *</label>
                    <input
                      type="text"
                      value={person.nom}
                      onChange={(e) => handleEditPerson(index, 'nom', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Prénoms *</label>
                    <input
                      type="text"
                      value={person.prenoms}
                      onChange={(e) => handleEditPerson(index, 'prenoms', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Fonction *</label>
                    <SearchableSelect
                      value={person.fonction}
                      onChange={(val) => handleEditPerson(index, 'fonction', val)}
                      options={fonctionOptions}
                      placeholder="Rechercher une fonction..."
                      allowCustom={true}
                      groupByCategory={personType === 'employe'}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Nationalité</label>
                    <input
                      type="text"
                      value={person.nationalite}
                      onChange={(e) => handleEditPerson(index, 'nationalite', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Téléphone</label>
                    <input
                      type="tel"
                      value={person.telephone}
                      onChange={(e) => handleEditPerson(index, 'telephone', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={person.email}
                      onChange={(e) => handleEditPerson(index, 'email', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      <span className="flex items-center">
                        <Linkedin className="w-3 h-3 mr-1 text-[#0077B5]" />
                        Profil LinkedIn
                      </span>
                    </label>
                    <input
                      type="url"
                      value={person.linkedin || ''}
                      onChange={(e) => handleEditPerson(index, 'linkedin', e.target.value)}
                      placeholder="https://linkedin.com/in/..."
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      <span className="flex items-center">
                        <FileText className="w-3 h-3 mr-1 text-[#197ca8]" />
                        CV
                      </span>
                    </label>
                    <CVDropZone
                      cv={person.cv}
                      cvFileName={person.cvFileName}
                      onFileChange={(url, fileName) => {
                        handleEditPerson(index, 'cv', url);
                        handleEditPerson(index, 'cvFileName', fileName);
                      }}
                      onRemove={() => {
                        handleEditPerson(index, 'cv', '');
                        handleEditPerson(index, 'cvFileName', '');
                      }}
                      disabled={false}
                    />
                  </div>
                </>
              )}
              
              {showEquity && shareLabels.shareLabel && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {shareLabels.shareLabel}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={person.nombreActions || ''}
                      onChange={(e) => handleEditPerson(index, 'nombreActions', parseInt(e.target.value) || 0)}
                      placeholder="Ex: 1000"
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {shareLabels.percentLabel}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={person.pourcentageActions || ''}
                      onChange={(e) => handleEditPerson(index, 'pourcentageActions', parseFloat(e.target.value) || 0)}
                      placeholder="Ex: 25.5"
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                    />
                  </div>
                </>
              )}

              {showContract && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Type contrat</label>
                  <select
                    value={person.typeContrat}
                    onChange={(e) => handleEditPerson(index, 'typeContrat', e.target.value)}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                  >
                    <option value="">Sélectionner...</option>
                    <option value="CDI">CDI</option>
                    <option value="CDD">CDD</option>
                    <option value="Consultant">Consultant</option>
                    <option value="Stage">Stage</option>
                  </select>
                </div>
              )}
            </div>

            <div className="mt-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">Adresse</label>
              <textarea
                value={person.adresse}
                onChange={(e) => handleEditPerson(index, 'adresse', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
              />
            </div>
          </div>
        ))}
      </div>

      {/* Ajouter une nouvelle personne */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-3">
          Ajouter un nouveau {getPersonTypeLabel().toLowerCase()}
        </h4>
        
        {/* Sélecteur type de détenteur pour les actionnaires */}
        {personType === 'actionnaire' && (
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-700 mb-2">Type de détenteur</label>
            <div className="flex space-x-4">
              <label className="flex items-center cursor-pointer p-2 rounded border border-gray-200 hover:bg-white">
                <input
                  type="radio"
                  name="newTypeDetenteur"
                  checked={newPerson.typeDetenteur !== 'morale'}
                  onChange={() => setNewPerson({...newPerson, typeDetenteur: 'physique'})}
                  className="mr-2 text-[#197ca8]"
                />
                <User className="w-4 h-4 mr-1 text-gray-600" />
                <span className="text-sm">Personne physique</span>
              </label>
              <label className="flex items-center cursor-pointer p-2 rounded border border-gray-200 hover:bg-white">
                <input
                  type="radio"
                  name="newTypeDetenteur"
                  checked={newPerson.typeDetenteur === 'morale'}
                  onChange={() => setNewPerson({...newPerson, typeDetenteur: 'morale'})}
                  className="mr-2 text-purple-600"
                />
                <Building2 className="w-4 h-4 mr-1 text-gray-600" />
                <span className="text-sm">Personne morale</span>
              </label>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Champs pour personne morale */}
          {newPerson.typeDetenteur === 'morale' ? (
            <>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Raison sociale *</label>
                <input
                  type="text"
                  value={newPerson.raisonSociale || ''}
                  onChange={(e) => setNewPerson({...newPerson, raisonSociale: e.target.value})}
                  placeholder="Nom de l'entreprise"
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Forme juridique</label>
                <select
                  value={newPerson.formeJuridique || ''}
                  onChange={(e) => setNewPerson({...newPerson, formeJuridique: e.target.value})}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                >
                  <option value="">Sélectionner...</option>
                  <option value="SARL">SARL</option>
                  <option value="SA">SA</option>
                  <option value="SAS">SAS</option>
                  <option value="SNC">SNC</option>
                  <option value="GIE">GIE</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Fonction/Qualité *</label>
                <SearchableSelect
                  value={newPerson.fonction || ''}
                  onChange={(val) => setNewPerson({...newPerson, fonction: val})}
                  options={fonctionOptions}
                  placeholder="Rechercher une fonction..."
                  allowCustom={true}
                  groupByCategory={false}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">N° RCCM</label>
                <input
                  type="text"
                  value={newPerson.rccm || ''}
                  onChange={(e) => setNewPerson({...newPerson, rccm: e.target.value})}
                  placeholder="CD/KIN/RCCM/..."
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">ID National</label>
                <input
                  type="text"
                  value={newPerson.idnat || ''}
                  onChange={(e) => setNewPerson({...newPerson, idnat: e.target.value})}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Siège social</label>
                <input
                  type="text"
                  value={newPerson.siegeSocial || ''}
                  onChange={(e) => setNewPerson({...newPerson, siegeSocial: e.target.value})}
                  placeholder="Adresse du siège social"
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Représentant légal</label>
                <input
                  type="text"
                  value={newPerson.representantLegal || ''}
                  onChange={(e) => setNewPerson({...newPerson, representantLegal: e.target.value})}
                  placeholder="Nom complet"
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Fonction du représentant</label>
                <input
                  type="text"
                  value={newPerson.representantFonction || ''}
                  onChange={(e) => setNewPerson({...newPerson, representantFonction: e.target.value})}
                  placeholder="Ex: Directeur Général"
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={newPerson.telephone || ''}
                  onChange={(e) => setNewPerson({...newPerson, telephone: e.target.value})}
                  placeholder="+243 ..."
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={newPerson.email || ''}
                  onChange={(e) => setNewPerson({...newPerson, email: e.target.value})}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>
            </>
          ) : (
            <>
              {/* Champs pour personne physique */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Nom *</label>
                <input
                  type="text"
                  value={newPerson.nom || ''}
                  onChange={(e) => setNewPerson({...newPerson, nom: e.target.value})}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Prénoms *</label>
                <input
                  type="text"
                  value={newPerson.prenoms || ''}
                  onChange={(e) => setNewPerson({...newPerson, prenoms: e.target.value})}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Fonction *</label>
                <SearchableSelect
                  value={newPerson.fonction || ''}
                  onChange={(val) => setNewPerson({...newPerson, fonction: val})}
                  options={fonctionOptions}
                  placeholder="Rechercher une fonction..."
                  allowCustom={true}
                  groupByCategory={personType === 'employe'}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={newPerson.telephone || ''}
                  onChange={(e) => setNewPerson({...newPerson, telephone: e.target.value})}
                  placeholder="+243 ..."
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={newPerson.email || ''}
                  onChange={(e) => setNewPerson({...newPerson, email: e.target.value})}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  <span className="flex items-center">
                    <Linkedin className="w-3 h-3 mr-1 text-[#0077B5]" />
                    Profil LinkedIn
                  </span>
                </label>
                <input
                  type="url"
                  value={newPerson.linkedin || ''}
                  onChange={(e) => setNewPerson({...newPerson, linkedin: e.target.value})}
                  placeholder="https://linkedin.com/in/..."
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  <span className="flex items-center">
                    <FileText className="w-3 h-3 mr-1 text-[#197ca8]" />
                    CV
                  </span>
                </label>
                <CVDropZone
                  cv={newPerson.cv}
                  cvFileName={newPerson.cvFileName}
                  onFileChange={(url, fileName) => setNewPerson({...newPerson, cv: url, cvFileName: fileName})}
                  onRemove={() => setNewPerson({...newPerson, cv: '', cvFileName: ''})}
                  disabled={false}
                />
              </div>
            </>
          )}
          
          {/* Champs d'actionnariat selon la forme juridique */}
          {showEquity && shareLabels.shareLabel && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {shareLabels.shareLabel}
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={newPerson.nombreActions || ''}
                  onChange={(e) => setNewPerson({...newPerson, nombreActions: parseInt(e.target.value) || 0})}
                  placeholder="Ex: 1000"
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {shareLabels.percentLabel}
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={newPerson.pourcentageActions || ''}
                  onChange={(e) => setNewPerson({...newPerson, pourcentageActions: parseFloat(e.target.value) || 0})}
                  placeholder="Ex: 25.5"
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>
            </>
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={handleAddPerson}
            disabled={newPerson.typeDetenteur === 'morale' 
              ? !newPerson.raisonSociale || !newPerson.fonction
              : !newPerson.nom || !newPerson.prenoms || !newPerson.fonction
            }
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#197ca8] hover:bg-[#156a92] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#197ca8] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
};